export interface ScreenWakeLock {
	start: () => void;
	stop: () => void;
}

export function useScreenWakeLock(): ScreenWakeLock {
	let started = false;
	let sentinel: WakeLockSentinel | null = null;
	let requestPending = false;
	let acquisitionGeneration = 0;

	const releaseSentinel = (sentinelToRelease: WakeLockSentinel) => {
		try {
			void sentinelToRelease.release().catch((error) => {
				console.warn("screen wake lock release failed", error);
			});
		} catch (error) {
			console.warn("screen wake lock release failed", error);
		}
	};

	const requestWakeLock = () => {
		if (!started || sentinel || requestPending || document.visibilityState !== "visible") return;
		const requestGeneration = ++acquisitionGeneration;
		requestPending = true;

		let request: Promise<WakeLockSentinel>;
		try {
			request = navigator.wakeLock.request("screen");
		} catch (error) {
			requestPending = false;
			console.warn("screen wake lock failed", error);
			return;
		}

		request
			.then((requestedSentinel) => {
				if (!started || requestGeneration !== acquisitionGeneration || document.visibilityState !== "visible") {
					releaseSentinel(requestedSentinel);
					return;
				}
				sentinel = requestedSentinel;
				requestedSentinel.addEventListener?.(
					"release",
					() => {
						if (sentinel !== requestedSentinel) return;
						sentinel = null;
						requestWakeLock();
					},
					{ once: true }
				);
			})
			.catch((error) => {
				console.warn("screen wake lock failed", error);
			})
			.finally(() => {
				if (requestGeneration === acquisitionGeneration) requestPending = false;
			});
	};

	const handleVisibilityChange = () => {
		if (!started) return;
		if (document.visibilityState === "hidden") {
			acquisitionGeneration += 1;
			requestPending = false;
			sentinel = null;
			return;
		}
		requestWakeLock();
	};

	const start = () => {
		if (typeof navigator === "undefined" || !("wakeLock" in navigator) || typeof navigator.wakeLock?.request !== "function") return;
		if (started) return;
		started = true;
		document.addEventListener("visibilitychange", handleVisibilityChange);
		requestWakeLock();
	};

	const stop = () => {
		if (!started) return;
		started = false;
		acquisitionGeneration += 1;
		requestPending = false;
		document.removeEventListener("visibilitychange", handleVisibilityChange);
		if (sentinel) {
			releaseSentinel(sentinel);
			sentinel = null;
		}
	};

	return { start, stop };
}
